import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DateTimeRangePicker from '@wojtekmaj/react-datetimerange-picker';
import { DateTime } from 'luxon';
import propTypes from 'prop-types';
import _ from 'lodash';

import { useTranslation } from '../../localization/useTranslation';
import './timepicker.css';
import {
  Checkbox,
  Button,
  ButtonVariant,
  Popover,
  PopoverPosition
} from '@patternfly/react-core';
import { TimeOffsetTranslation, TimeOffset } from '../../services/constants';
import {
  formatDates,
  handleCommonRelativeTimeChange
} from './utils';
import CustomRelativeInput from './CustomRelativeInput';
import SelectV2 from 'components/Select/SelectV2';

const commonRelativeTime = _.map(TimeOffsetTranslation, (text, value) => ({ value, text }));

export const TimeSelector = ({ initialTimeSpan, onApply }) => {
  const T = useTranslation();

  const [timeRange, setTimeRange] = useState({ from: DateTime.now(), to: DateTime.now() });
  const [isCustomRelativeInputOpen, setIsCustomRelativeInputOpen] =
    useState(false);
  const [currentRelativeTime, setCurrentRelativeTime] = useState('');

  const handleCommonRelativeInputChange = useCallback((isCustomRelativeInputOpen) => {
    if (isCustomRelativeInputOpen) {
      handleCommonRelativeTimeChange(TimeOffset.CUSTOM, setTimeRange, timeRange);
    }
  }, [timeRange]);

  useEffect(() => {
    handleCommonRelativeInputChange(isCustomRelativeInputOpen);
  }, [handleCommonRelativeInputChange, isCustomRelativeInputOpen]);

  const handleCalendarOpen = () => {

    handleCommonRelativeTimeChange(TimeOffset.CUSTOM, setTimeRange,
      { from: timeRange.from.set({ hours: 0, minutes: 0 }), to: timeRange.to.set({ hours: 0, minutes: 0 }) });
    setIsCustomRelativeInputOpen(false);
  };

  useEffect(() => {
    setTimeRange(initialTimeSpan);
  }, [initialTimeSpan]);

  const formattedDates = useMemo(() => {
    return formatDates(timeRange);
  }, [timeRange]);

  return (
    <Popover
      aria-label="Time range selector"
      minWidth="450px"
      hideOnOutsideClick={false}
      position={PopoverPosition.bottomEnd}
      bodyContent={
        <div>
          <div className="timerange-wrapper">
            <div>
              <DateTimeRangePicker
                onChange={(arr) => setTimeRange({ from: DateTime.fromJSDate(arr[0]), to: DateTime.fromJSDate(arr[1]) })}
                value={[timeRange.from.toJSDate(), timeRange.to.toJSDate()]}
                format="y-MM-dd"
                disableClock={false}
                onCalendarOpen={handleCalendarOpen}
                rangeDivider={<span className="range-divider">{ T.common.to } </span>}
              />
            </div>

            <div className="form-select">
              <SelectV2
                value={currentRelativeTime}
                options={commonRelativeTime}
                isDisabled={isCustomRelativeInputOpen}
                placeholder={T.graph.toolbar.timepicker.relative.placeholder}
                onChange={(timeOffset) => {
                  setCurrentRelativeTime(timeOffset);
                  handleCommonRelativeTimeChange(timeOffset, setTimeRange, timeRange);
                }}
              />
            </div>
            <div>
              <Checkbox
                label={T.graph.toolbar.timepicker.relative.checkbox}
                isChecked={isCustomRelativeInputOpen}
                onChange={(e) => setIsCustomRelativeInputOpen(e.target.checked)}
                aria-label="controlled checkbox"
                id="check-1"
                name="check1"
              />
            </div>

            { isCustomRelativeInputOpen && (
              <CustomRelativeInput
                onTimeSpanChange={setTimeRange}
              />
            ) }
            <Button
              variant={ButtonVariant.primary}
              className="apply-btn"
              onClick={() => onApply(timeRange)}
            >
              { T.common.apply }
            </Button>
          </div>
        </div>
      }
    >
      <Button className="mt-10">
        { formattedDates.from } - { formattedDates.to }
      </Button>
    </Popover>
  );
};

TimeSelector.propTypes = {
  onApply: propTypes.func.isRequired
};
